/* =========================================================================
   ducks-n-stuff — header behaviour
   · one shared directory panel, opened by any door that has children
   · mobile drawer with a focus trap
   · sticky shadow once the page has scrolled
   ========================================================================= */

class DnsHeader extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('#DnsMegaPanel');
    this.triggers = Array.from(this.querySelectorAll('[data-dns-mega-trigger]'));
    this.columns = Array.from(this.querySelectorAll('[data-dns-mega-col]'));
    this.closeTimer = null;

    if (this.panel && this.triggers.length) this.bindMega();
    this.bindSticky();
  }

  disconnectedCallback() {
    if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    clearTimeout(this.closeTimer);
  }

  /* ---- directory panel -------------------------------------------------- */

  bindMega() {
    this.triggers.forEach((trigger) => {
      const index = trigger.dataset.dnsMegaTrigger;

      trigger.addEventListener('mouseenter', () => this.openMega(index));
      trigger.addEventListener('focus', () => this.openMega(index));

      // On touch the first tap opens the panel, the second follows the link.
      trigger.addEventListener('click', (event) => {
        if (!window.matchMedia('(hover: none)').matches) return;
        if (trigger.getAttribute('aria-expanded') === 'true') return;
        event.preventDefault();
        this.openMega(index);
      });
    });

    this.addEventListener('mouseleave', () => this.scheduleClose());
    this.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));

    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.closeMega();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.panel.classList.contains('is-open')) {
        this.closeMega();
        this.triggers.find((t) => t.getAttribute('aria-expanded') === 'true')?.focus();
      }
    });
  }

  openMega(index) {
    clearTimeout(this.closeTimer);
    this.panel.hidden = false;
    // force a frame so the transition runs from the hidden state
    requestAnimationFrame(() => this.panel.classList.add('is-open'));

    this.triggers.forEach((t) => t.setAttribute('aria-expanded', String(t.dataset.dnsMegaTrigger === index)));

    const focused = this.columns.filter((c) => c.dataset.dnsMegaCol === index);
    this.columns.forEach((c) => c.classList.toggle('is-focused', c.dataset.dnsMegaCol === index));
    this.panel.classList.toggle('has-focus', focused.length > 0 && this.columns.length > 1);
  }

  scheduleClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.closeMega(), 180);
  }

  closeMega() {
    if (!this.panel) return;
    clearTimeout(this.closeTimer);
    this.panel.classList.remove('is-open', 'has-focus');
    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.columns.forEach((c) => c.classList.remove('is-focused'));
    // wait out the fade before removing it from the a11y tree
    setTimeout(() => {
      if (!this.panel.classList.contains('is-open')) this.panel.hidden = true;
    }, 200);
  }

  /* ---- sticky shadow ---------------------------------------------------- */

  bindSticky() {
    if (!this.classList.contains('dns-header--sticky')) return;
    this.scrollHandler = () => {
      this.classList.toggle('is-stuck', window.scrollY > 4);
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    this.scrollHandler();
  }
}

if (!customElements.get('dns-header')) customElements.define('dns-header', DnsHeader);

/* =========================================================================
   Mobile drawer
   ========================================================================= */

(() => {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function init() {
    const drawer = document.getElementById('DnsDrawer');
    if (!drawer || drawer.dataset.dnsBound) return;
    drawer.dataset.dnsBound = 'true';

    const panel = drawer.querySelector('.dns-drawer__panel');
    let lastFocused = null;

    const open = (trigger) => {
      lastFocused = trigger || document.activeElement;
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      document.body.style.overflow = 'hidden';
      document.body.classList.add('dns-drawer-active');
      document.querySelectorAll('[data-dns-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      panel.querySelector(FOCUSABLE)?.focus();
    };

    const close = () => {
      drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      document.body.classList.remove('dns-drawer-active');
      document.querySelectorAll('[data-dns-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      setTimeout(() => {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 280);
      lastFocused?.focus();
    };

    document.querySelectorAll('[data-dns-drawer-open]').forEach((btn) => {
      btn.addEventListener('click', () => open(btn));
    });

    drawer.querySelectorAll('[data-dns-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', close);
    });

    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Close when the viewport grows back to the desktop nav.
    window.matchMedia('(min-width: 1024px)').addEventListener('change', (e) => {
      if (e.matches && drawer.classList.contains('is-open')) close();
    });
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState !== 'loading') init();
})();
